import { useEffect, useState } from "react";
import { demoApi, demoTenantId } from "@/api/client";
import type { Account, PaymentMethod, Vendor } from "@/api/types";
import { readReferenceSnapshot, writeReferenceSnapshot } from "@/offline/referenceCache";
import { describeReadFailure } from "@/lib/apiFailure";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";

export type ReferenceDataStatus = "loading" | "ready" | "error";

export interface UseReferenceDataResult {
  vendors: Vendor[];
  accounts: Account[];
  /** Empty when the route 404s (not deployed to this environment yet) or
   *  when nothing has been seeded — either way, callers treat it the same:
   *  skip the payment-method step rather than block on it. */
  paymentMethods: PaymentMethod[];
  /**
   * The tenant's entity, discovered rather than configured.
   *
   * This replaces the VITE_DEMO_ENTITY_ID build-time env var, which was a
   * stopgap from before GET /api/demo/entities existed. Discovering it means one
   * fewer value to get wrong per deployment, and it is what lets the e2e suite
   * point at its own tenant without anyone hand-copying a guid.
   *
   * Null while loading, or if the tenant genuinely has no entity — in which case
   * submitting is blocked rather than sent with a guessed id.
   */
  entityId: string | null;
  status: ReferenceDataStatus;
  error: string | null;
  /**
   * True when these lists came from the last successful load rather than this one.
   *
   * Exposed so the screen can SAY so rather than quietly presenting month-old pick
   * lists as live — `rules/ui-breadcrumbs.md` §1/§5: a tester (and an operator)
   * reads declared state, and a breadcrumb that lies is worse than none.
   */
  fromCache: boolean;
}

/**
 * Fetches the constrained vendor + category (account) + payment-method
 * pick-list data once. Shared by the laptop dashboard (to resolve names for
 * the entries feed) and the phone quick-entry flow (as the pick-list source
 * itself).
 *
 * Reference data does not need polling — vendors/accounts/payment methods
 * are seeded once per booth and do not change mid-show.
 *
 * Payment methods are fetched independently of vendors/accounts and never
 * fail this hook's overall `status`: `GET /api/demo/payment-methods` is a
 * newer route that may not be deployed everywhere yet (see api/client.ts
 * `getPaymentMethods`), and vendor/category pickers — the two things a
 * visitor absolutely cannot complete an entry without — must keep working
 * even if that route is missing or erroring. Any payment-methods failure
 * (404 or otherwise) degrades to an empty list, logged to the console for
 * whoever is at the booth laptop, never a blank/crashed quick-entry screen.
 */
export function useReferenceData(): UseReferenceDataResult {
  // ef-047-1 — replaces this hook's own `entities[0]` guess (see the `load()` remarks
  // this used to carry) with the shared, persisted switcher. `entityStatus` gates the
  // effect below so vendors/accounts/payment-methods wait for the switcher to resolve
  // once, the same way this hook always waited for its own `getEntities()` call before.
  const { entityId: selectedEntityId, status: entityStatus } = useSelectedEntity();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [status, setStatus] = useState<ReferenceDataStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    if (entityStatus === "loading") return;
    let cancelled = false;
    const controller = new AbortController();
    // Payment methods resolve on their own track (see this hook's remarks). These
    // two let whichever request finishes LAST write a complete snapshot, rather
    // than the vendor load persisting an empty payment-method list and the demo's
    // third beat — "a different card than usual" — silently losing its picker on
    // the next offline reload.
    let methodsForCache: PaymentMethod[] = [];
    let wroteSnapshot = false;

    async function loadPaymentMethods(): Promise<void> {
      try {
        const result = await demoApi.getPaymentMethods(controller.signal);
        if (cancelled) return;
        if (result.status === "unavailable") {
          console.warn(
            "GET /api/demo/payment-methods returned 404 — payment method step hidden for this session.",
          );
          setPaymentMethods([]);
          return;
        }
        setPaymentMethods(result.methods);
        methodsForCache = result.methods;
        rewriteSnapshotIfLoaded();
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;

        // A THROW is not a 404. The branch above handles a reachable server saying the
        // route does not exist, and hiding the step is right there. This is the request
        // never completing — offline, almost always — and clearing the list here is what
        // made the first version of this fix ship a half-repair: measured on 2026-09-06,
        // the offline reload came back with the vendor and category pickers restored and
        // the payment step MISSING (29 controls where online has 33), because this
        // handler ran after the cache had already supplied them. That is booth beat 3,
        // "you paid this on a different card than usual", quietly deleted in exactly the
        // no-signal condition the beat is meant to prove.
        const cached = readReferenceSnapshot(demoTenantId());
        if (cached && cached.paymentMethods.length > 0) {
          setPaymentMethods(cached.paymentMethods);
          methodsForCache = cached.paymentMethods;
          return;
        }

        console.warn(
          "Could not load payment methods — payment method step hidden for this session.",
          err,
        );
        setPaymentMethods([]);
      }
    }

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const [vendorList, accountList] = await Promise.all([
          demoApi.getVendors(controller.signal),
          demoApi.getAccounts(controller.signal),
        ]);
        if (cancelled) return;
        setVendors(vendorList);
        setAccounts(accountList);
        // ef-047-1 — was this hook's own `entities[0]` guess (`GetEntitiesAsync`'s
        // `ORDER BY name`, so "Larson Family Farms" before "Larson Trucking LLC");
        // now the shared, persisted switcher's own resolution (`useSelectedEntity.ts`:
        // the tenant's default entity, else the same first-active fallback this used to
        // inline here). Still no picker AT THE BOOTH specifically — QuickEntry never
        // renders `EntitySwitcher` — but every OTHER screen this hook feeds now agrees
        // with whichever entity the farmer last picked, live.
        setEntityId(selectedEntityId);
        setFromCache(false);
        setStatus("ready");

        // Write through, so the NEXT mount survives having no network. Payment
        // methods load on their own track and may not have landed yet; whichever
        // of the two finishes last writes the complete snapshot.
        writeReferenceSnapshot(demoTenantId(), {
          vendors: vendorList,
          accounts: accountList,
          paymentMethods: methodsForCache,
          entityId: selectedEntityId,
        });
        wroteSnapshot = true;
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;

        // The whole point of this module. Before falling back to an error screen
        // that a farmer with no signal cannot get past, use the last lists the API
        // gave us. See offline/referenceCache.ts for the measurement that produced
        // this, and why the existing offline check could not see it.
        const cached = readReferenceSnapshot(demoTenantId());
        if (cached) {
          setVendors(cached.vendors);
          setAccounts(cached.accounts);
          if (cached.paymentMethods.length > 0) setPaymentMethods(cached.paymentMethods);
          setEntityId(cached.entityId);
          setFromCache(true);
          setStatus("ready");
          return;
        }

        setStatus("error");
        setError(describeReadFailure(err));
      }
    }

    /** Only ever ADDS the payment methods to a snapshot the successful vendor/account
     *  load already wrote — never creates one, because a snapshot without vendors and
     *  categories cannot complete an entry (referenceCache rejects those on read). */
    function rewriteSnapshotIfLoaded(): void {
      if (!wroteSnapshot) return;
      const existing = readReferenceSnapshot(demoTenantId());
      if (!existing) return;
      writeReferenceSnapshot(demoTenantId(), { ...existing, paymentMethods: methodsForCache });
    }

    void load();
    void loadPaymentMethods();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedEntityId, entityStatus]);

  return { vendors, accounts, paymentMethods, entityId, status, error, fromCache };
}
